(function (root, factory) {
  const createPageTrackingMediaInteraction = factory();

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = createPageTrackingMediaInteraction;
    return;
  }

  if (root) {
    root.createPageTrackingMediaInteraction = createPageTrackingMediaInteraction;
  }
})(typeof window !== 'undefined' ? window : undefined, function createPageTrackingMediaInteractionFactory() {
  return function createPageTrackingMediaInteraction(core, options) {
    const currentOptions = options || {};
    const pageIdentity = currentOptions.pageIdentity || {
      pageName: '',
      subPageName: ''
    };
    const videoStates = new WeakMap();

    function getVideoState(videoElement) {
      let state;

      if (!videoElement) {
        return null;
      }

      state = videoStates.get(videoElement);

      if (!state) {
        state = {
          isPlaying: false,
          sessionClosed: true
        };
        videoStates.set(videoElement, state);
      }

      return state;
    }

    function buildMediaInteractionData(videoElement, now, playbackCompleted) {
      const sourceUrl = core.utils.getVideoSourceUrl(videoElement);
      const duration = core.utils.normalizeMediaDuration(videoElement && videoElement.duration);
      let currentTime = Math.max(0, Number(videoElement && videoElement.currentTime) || 0);

      if (playbackCompleted && duration > 0) {
        currentTime = duration;
      }

      const watchMetrics = core.utils.resolveMediaWatchMetrics(currentTime, duration);

      return core.buildDataInfo({
        type: core.config.eventTypes && core.config.eventTypes.mediaInteraction,
        pageName: pageIdentity.pageName,
        subPageName: pageIdentity.subPageName,
        mediaName: core.utils.extractMediaNameFromUrl(sourceUrl),
        mediaType: 'video',
        productName: sourceUrl,
        mediaDuration: duration,
        watchDuration: watchMetrics.watchDuration,
        progress: watchMetrics.progress
      }, now);
    }

    function reportVideoEnd(videoElement, state, useBeacon, playbackCompleted) {
      if (!videoElement || !state || state.sessionClosed) {
        return false;
      }

      state.isPlaying = false;
      state.sessionClosed = true;
      core.markActivity(Date.now());

      const dataInfo = buildMediaInteractionData(videoElement, new Date(), Boolean(playbackCompleted));
      const watchDuration = Number(dataInfo.watchDuration);

      if (watchDuration === 0) {
        return false;
      }

      return core.sendTrackingEvent({
        basicInfo: core.buildBasicInfo(),
        dataInfo: dataInfo
      }, true);
    }

    function handleVideoPlay(event) {
      const videoElement = event && event.target;
      const state = getVideoState(videoElement);

      if (!videoElement || !state || state.isPlaying) {
        return false;
      }

      state.isPlaying = true;
      state.sessionClosed = false;

      return true;
    }

    function handleVideoPause(event) {
      const videoElement = event && event.target;
      const state = getVideoState(videoElement);

      if (!videoElement || !state || !state.isPlaying) {
        return false;
      }

      return reportVideoEnd(videoElement, state, false, Boolean(videoElement.ended));
    }

    function handleVideoEnded(event) {
      const videoElement = event && event.target;
      const state = getVideoState(videoElement);

      if (!videoElement || !state || !state.isPlaying) {
        return false;
      }

      return reportVideoEnd(videoElement, state, false, true);
    }

    function flushActiveMediaSessions(useBeacon) {
      const videos = core.document && typeof core.document.querySelectorAll === 'function'
        ? core.document.querySelectorAll('video')
        : [];

      Array.prototype.forEach.call(videos, function flushOne(videoElement) {
        const state = getVideoState(videoElement);

        if (state && state.isPlaying && !state.sessionClosed) {
          reportVideoEnd(videoElement, state, useBeacon, false);
        }
      });

      return true;
    }

    function handleVisibilityHidden() {
      if (!core.document || core.document.visibilityState !== 'hidden') {
        return false;
      }

      flushActiveMediaSessions(true);
      return true;
    }

    function bindMediaEvents(sessionModule, sessionCallbacks) {
      if (!core.document || typeof core.document.addEventListener !== 'function') {
        return false;
      }

      core.document.addEventListener('play', handleVideoPlay, true);
      core.document.addEventListener('pause', handleVideoPause, true);
      core.document.addEventListener('ended', handleVideoEnded, true);

      core.document.addEventListener('visibilitychange', handleVisibilityHidden);
      core.window.addEventListener('pagehide', function handlePageHide() {
        flushActiveMediaSessions(true);
      });
      core.window.addEventListener('beforeunload', function handleBeforeUnload() {
        flushActiveMediaSessions(true);
      });

      return true;
    }

    return {
      getVideoState,
      buildMediaInteractionData,
      reportVideoEnd,
      handleVideoPlay,
      handleVideoPause,
      handleVideoEnded,
      flushActiveMediaSessions,
      handleVisibilityHidden,
      bindMediaEvents
    };
  };
});
